/**
 * Projektregeln — Ereignis, Bedingung, Aktion (Paket 11).
 * Eigenbau an der Form von Tremor AuditRules, mit Herkunftshinweis.
 */
// Herkunft: https://insights.tremor.so/settings/audit (AuditRules-Form)
import { useMemo, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDownToDot,
  CircleArrowOutUpRight,
  SquareFunction,
} from "lucide-react";
import { VORGAENGE } from "@cubic/shared";
import { api } from "@/lib/api";
import { Seitenkopf } from "@/components/Seitenkopf";
import { Button } from "@/components/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/Dialog";
import { Input } from "@/components/Input";
import { Label } from "@/components/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Select";
import { Card } from "@/components/Card";
import { Badge } from "@/components/Badge";
import { Leerzustand } from "@/components/Leerzustand";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { meldungZu } from "@/lib/meldung";
import { grundVon, istNetzfehler } from "@/lib/api";

const SCHLUESSEL = (projektId: string) =>
  ["cubic", "regeln", projektId] as const;

export function RegelnSeite() {
  const { projektId } = useParams({
    from: "/_app/projekte/$projektId/regeln",
  });
  const client = useQueryClient();
  const [name, setzeName] = useState("");
  const [vorgang, setzeVorgang] = useState<string>("status_gewechselt");
  const [aktionInhalt, setzeAktionInhalt] = useState(
    "Automatisch: Statuswechsel bemerkt.",
  );
  const [loeschKandidat, setzeLoeschKandidat] = useState<
    { id: string; name: string; version: number } | null
  >(null);
  const [fehler, setzeFehler] = useState<ReturnType<typeof meldungZu> | null>(
    null,
  );

  const liste = useQuery({
    queryKey: SCHLUESSEL(projektId),
    queryFn: () => api.regeln.liste.query({ projektId }),
    enabled: Boolean(projektId),
  });
  const laeufe = useQuery({
    queryKey: [...SCHLUESSEL(projektId), "laeufe"],
    queryFn: () => api.regeln.laeufe.query({ projektId, grenze: 20 }),
    enabled: Boolean(projektId),
  });

  const anlegen = useMutation({
    mutationFn: () =>
      api.regeln.anlegen.mutate({
        projektId,
        name: name.trim() || "Neue Regel",
        zustand: "aktiv",
        schritte: [
          {
            art: "ereignis",
            reihenfolge: 0,
            konfiguration: { vorgang },
          },
          {
            art: "aktion",
            reihenfolge: 1,
            konfiguration: {
              aktion: "kommentar_schreiben",
              inhalt: aktionInhalt,
            },
          },
        ],
      }),
    onSuccess: async () => {
      setzeName("");
      setzeFehler(null);
      await client.invalidateQueries({ queryKey: SCHLUESSEL(projektId) });
    },
    onError: (e) => {
      setzeFehler(
        meldungZu(grundVon(e), {
          netzfehler: istNetzfehler(e),
          fallback: e instanceof Error ? e.message : "Regel nicht angelegt",
        }),
      );
    },
  });

  const pausieren = useMutation({
    mutationFn: (r: { id: string; version: number; zustand: string }) =>
      r.zustand === "pausiert"
        ? api.regeln.fortsetzen.mutate({
            id: r.id,
            erwarteteVersion: r.version,
          })
        : api.regeln.pausieren.mutate({
            id: r.id,
            erwarteteVersion: r.version,
          }),
    onSuccess: async () => {
      setzeFehler(null);
      await client.invalidateQueries({ queryKey: SCHLUESSEL(projektId) });
    },
    onError: (e) => {
      setzeFehler(
        meldungZu(grundVon(e), {
          netzfehler: istNetzfehler(e),
          fallback:
            e instanceof Error ? e.message : "Regel konnte nicht geändert werden",
        }),
      );
    },
  });

  /*
   * Regeln kennen kein Archiv — `regeln.loeschen` gab es seit Paket 11, nur
   * ohne Knopf. Deshalb hier mit Rückfrage: Eine gelöschte Regel ist fort, und
   * ihre Läufe verlieren ihren Bezug.
   */
  const loeschen = useMutation({
    mutationFn: (r: { id: string; version: number }) =>
      api.regeln.loeschen.mutate({ id: r.id, erwarteteVersion: r.version }),
    onSuccess: async () => {
      setzeFehler(null);
      setzeLoeschKandidat(null);
      await client.invalidateQueries({ queryKey: SCHLUESSEL(projektId) });
    },
    onError: (e) => {
      setzeFehler(
        meldungZu(grundVon(e), {
          netzfehler: istNetzfehler(e),
          fallback:
            e instanceof Error ? e.message : "Regel konnte nicht gelöscht werden",
        }),
      );
    },
  });

  const letzteJeRegel = useMemo(() => {
    const map = new Map<string, { ergebnis: string; angelegtAm: Date | string }>();
    for (const l of laeufe.data ?? []) {
      if (!l.regelId) continue;
      if (!map.has(l.regelId)) {
        map.set(l.regelId, {
          ergebnis: l.ergebnis,
          angelegtAm: l.angelegtAm,
        });
      }
    }
    return map;
  }, [laeufe.data]);

  return (
    <div className="space-y-8">
      <Seitenkopf
        titel="Regeln"
        beschreibung="Wenn dieses Ereignis eintritt und die Bedingung gilt, dann diese Aktion."
        aktion={
          <Link
            to="/projekte/$id"
            params={{ id: projektId }}
            search={{ reiter: "puls" }}
            className="text-sm text-gray-500 underline"
          >
            Zum Projekt
          </Link>
        }
      />

      {fehler ? <Fehlerzeile meldung={fehler} /> : null}
      {liste.isError || laeufe.isError ? (
        <Fehlerzeile
          meldung={{
            ...meldungZu("intern", {
              fallback:
                liste.isError && laeufe.isError
                  ? "Regeln und Regelläufe konnten nicht geladen werden."
                  : liste.isError
                    ? "Regeln konnten nicht geladen werden."
                    : "Regelläufe konnten nicht geladen werden.",
            }),
            erneutVersuchen: true,
          }}
          onErneut={() => {
            void liste.refetch();
            void laeufe.refetch();
          }}
        />
      ) : null}

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
          Neue Regel
        </h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="regel-name">Name</Label>
            <Input
              id="regel-name"
              value={name}
              onChange={(e) => setzeName(e.target.value)}
              placeholder="z. B. Kommentar bei Statuswechsel"
            />
          </div>
          <div>
            <Label htmlFor="regel-vorgang">Ereignis</Label>
            <Select value={vorgang} onValueChange={setzeVorgang}>
              <SelectTrigger id="regel-vorgang" className="mt-1">
                <SelectValue placeholder="Vorgang" />
              </SelectTrigger>
              <SelectContent>
                {VORGAENGE.map((v) => (
                  <SelectItem key={v} value={v}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="relative space-y-0 pl-2">
          <div className="absolute bottom-4 left-[1.65rem] top-4 w-px bg-gray-200 dark:bg-gray-800" />
          <SchrittKarte
            farbe="border-l-orange-500"
            icon={<ArrowDownToDot className="size-4 text-orange-600" />}
            titel="Ereignis"
            text={`Wenn „${vorgang}" eintritt`}
          />
          <SchrittKarte
            farbe="border-l-sky-500"
            icon={<SquareFunction className="size-4 text-sky-600" />}
            titel="Bedingung"
            text="Keine zusätzliche Bedingung (immer)"
          />
          <SchrittKarte
            farbe="border-l-emerald-500"
            icon={
              <CircleArrowOutUpRight className="size-4 text-emerald-600" />
            }
            titel="Aktion"
            text="Kommentar schreiben"
          >
            <Input
              value={aktionInhalt}
              onChange={(e) => setzeAktionInhalt(e.target.value)}
              aria-label="Kommentartext"
            />
          </SchrittKarte>
        </div>

        <Button
          onClick={() => anlegen.mutate()}
          disabled={anlegen.isPending}
          isLoading={anlegen.isPending}
          data-testid="regel-anlegen"
        >
          Regel speichern und aktivieren
        </Button>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
          Angewandte Regeln
        </h2>
        {liste.isLoading ? (
          <p className="text-sm text-gray-500">Lade …</p>
        ) : liste.isError ? null : (liste.data?.length ?? 0) === 0 ? (
          <Leerzustand
            titel="Noch keine Regeln."
            hinweis="Lege die erste Automatisierung an — sie läuft im Namen deiner Sicht."
          />
        ) : (
          <ul className="space-y-3">
            {liste.data!.map((r) => {
              const lauf = letzteJeRegel.get(r.id);
              return (
                <li key={r.id}>
                  <Card className="p-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-50">
                          {r.name}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          {r.schritte
                            .map((s) => s.art)
                            .join(" → ")}
                        </p>
                        {lauf ? (
                          <p className="mt-1 text-xs text-gray-500">
                            Letzter Lauf: {lauf.ergebnis} ·{" "}
                            {new Date(lauf.angelegtAm).toLocaleString("de-DE")}
                          </p>
                        ) : (
                          <p className="mt-1 text-xs text-gray-400">
                            Noch kein Lauf
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant={
                            r.zustand === "aktiv"
                              ? "success"
                              : r.zustand === "pausiert"
                                ? "warning"
                                : "neutral"
                          }
                        >
                          {r.zustand}
                        </Badge>
                        <Button
                          variant="secondary"
                          onClick={() =>
                            pausieren.mutate({
                              id: r.id,
                              version: r.version,
                              zustand: r.zustand,
                            })
                          }
                        >
                          {r.zustand === "pausiert" ? "Fortsetzen" : "Pausieren"}
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() =>
                            setzeLoeschKandidat({
                              id: r.id,
                              name: r.name,
                              version: r.version,
                            })
                          }
                        >
                          Löschen
                        </Button>
                      </div>
                    </div>
                  </Card>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <Dialog
        open={loeschKandidat !== null}
        onOpenChange={(offen) => {
          if (!offen) setzeLoeschKandidat(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regel löschen</DialogTitle>
          </DialogHeader>
          <p className="text-sm leading-relaxed">
            „{loeschKandidat?.name}" wird gelöscht. Anders als bei Aufgaben und
            Meilensteinen gibt es hier kein Archiv — die Regel ist danach fort,
            und ihre bisherigen Läufe verlieren ihren Bezug.
          </p>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setzeLoeschKandidat(null)}
            >
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                loeschKandidat
                  ? loeschen.mutate({
                      id: loeschKandidat.id,
                      version: loeschKandidat.version,
                    })
                  : undefined
              }
              disabled={loeschen.isPending}
            >
              {loeschen.isPending ? "Löscht …" : "Löschen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SchrittKarte({
  farbe,
  icon,
  titel,
  text,
  children,
}: {
  farbe: string;
  icon: React.ReactNode;
  titel: string;
  text: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      className={`relative mb-3 rounded-md border border-gray-200 border-l-4 bg-white p-3 dark:border-gray-800 dark:bg-gray-950 ${farbe}`}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-gray-50 dark:bg-gray-900">
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {titel}
          </p>
          <p className="text-sm text-gray-900 dark:text-gray-50">{text}</p>
          {children ? <div className="mt-2">{children}</div> : null}
        </div>
      </div>
    </div>
  );
}
